import { Question, RawLayoutInfo } from './paper-data';

interface LayoutBlock {
  left: number;
  top: number;
  width: number;
  height: number;
  text?: string;
  url: string;

  canAdjust: boolean;
  marginBottom: number;
  answerWidth: number;
  answerHeight: number;
  renderAnswerHeight?: number;
  qid?: string;
  idx?: any;
};

interface LayoutPage { blocks: LayoutBlock[], top: number };

type LayoutPages = LayoutPage[];

enum TypeCanPut {
  CanPutAll = 1,
  CanPutPart = 2,
  CanPutNot = 3,
}

// 新增一页
function newLayoutPage(top: number) : LayoutPage {
  return { blocks: [], top };
}

// 判断该box是否应该放到下一个页，以及下一页顶部留空大小
function canPutInCurrentPage(box: LayoutBlock, heightInner: number, lastOffsetY: number): { can: TypeCanPut; top: number; } {
  const free = heightInner - lastOffsetY;
  if (box.canAdjust) {
    // 放不下
    if (free < box.height) {
      return { can: TypeCanPut.CanPutNot , top: 0 };
      // 只能放下题干
    } else if (free >= box.height && free < box.height + box.answerHeight) {
      return { can: TypeCanPut.CanPutPart, top: (box.height + box.answerHeight + box.marginBottom - free) }
      // 放下题干和空
    } else {
      return { can: TypeCanPut.CanPutAll, top: 0 };
    }
  } else {
    return { can: free >= box.height ? TypeCanPut.CanPutAll : TypeCanPut.CanPutNot, top: 0 }
  }
}

interface VisitObj {
  qid?: string;
  width?: number;
  height?: number;
  url?: string;
  idx?: any;
  answerWidth?: number;
  text?: string;
  canAdjust?: boolean;
  answerHeight?: number;
  marginBottom?: number;
}

function traversal(raw: RawLayoutInfo, visit: (obj: VisitObj) => void) {

  const { page_layout, group_data, head, student_area, has_homework_feedback, homework_feedback_layout } = raw;

  const DEFAULT_PADDING_BOTTOM = page_layout.block_padding;

  let obj: VisitObj;

  // 试卷头部
  obj = {
    width: head.layout.w,
    height: head.layout.h,
    url: head.oss_image,
    canAdjust: false,
    answerHeight: 0,
    answerWidth: 0,
    marginBottom: DEFAULT_PADDING_BOTTOM,
  }
  visit(obj);

  // 学生姓名区域
  obj = {
    width: student_area.layout.w,
    height: student_area.layout.h,
    canAdjust: false,
    url: student_area.oss_image,
    answerHeight: 0,
    answerWidth: 0,
    marginBottom: DEFAULT_PADDING_BOTTOM
  }
  visit(obj);

  // 遍历题型
  for (let group of group_data) {
    let obj: VisitObj = {
      width: 0,
      height: page_layout.group_title.layout.h,
      text: `${group.group_title.indexTitle}、${group.group_title.content}`,
      url: '',
      canAdjust: false,
      answerHeight: 0,
      answerWidth: 0,
      marginBottom: DEFAULT_PADDING_BOTTOM
    }

    visit(obj);

    // 遍历题型下属题目
    group.questions.forEach(question => traversalQuestion(question, visit, DEFAULT_PADDING_BOTTOM));
  }
}

function traversalQuestion(question: Question, visit: (obj: VisitObj) => void, paddingBottom) {
  let obj: VisitObj = {
    width: question.layout.w,
    height: question.layout.h,
    url: question.oss_image,
    qid: question.id,
    idx: question.index !=undefined ? question.index : -1,
    canAdjust: question.has_solving_answer_area,
    marginBottom: paddingBottom,
    answerWidth: question.solving_answer_area ? question.solving_answer_area.layout.w : 0,
    answerHeight: question.has_solving_answer_area ? question.solving_answer_area.layout.h: 0
  }

  visit(obj);

  for (let subq of question.sub_qs || []) {
    traversalQuestion(subq, visit, paddingBottom);
  }
}

function layout(raw: RawLayoutInfo): LayoutPages {
  const { page_layout } = raw;

  const A4_HEIGHT_INNER = page_layout.h - page_layout.top_padding - page_layout.bottom_padding;

  let lastOffsetY = 0;
  let pages: LayoutPages = [];
  let page: LayoutPage = newLayoutPage(0);
  
  const visit = (obj) => {
    let block: LayoutBlock = {
      left: 0,
      top: lastOffsetY,
      width: obj.width,
      height: obj.height,
      canAdjust: obj.canAdjust,
      url: obj.url,
      marginBottom: obj.marginBottom,
      answerWidth: obj.answerWidth,
      answerHeight: obj.answerHeight,
      qid: obj.qid,
      idx: obj.idx,
      text: obj.text
    }
    // console.log(`lastOffsetY: ${lastOffsetY}`)

    const { can, top } = canPutInCurrentPage(block, A4_HEIGHT_INNER, lastOffsetY);
    if (can == TypeCanPut.CanPutNot) {
      pages.push(page);

      const check = canPutInCurrentPage(block, A4_HEIGHT_INNER, 0);
      if (check.can == TypeCanPut.CanPutNot) {
        throw new Error('存在块一页放不下的情况');
      }

      lastOffsetY = 0;
      page = newLayoutPage(lastOffsetY);

      visit(obj);
      
    } else if (can == TypeCanPut.CanPutAll) {

      block.renderAnswerHeight = block.answerHeight
      page.blocks.push(block);
      lastOffsetY += (block.height + block.answerHeight + block.marginBottom);

    } else if (can == TypeCanPut.CanPutPart) {
      const blankPageNum = Math.floor(top / A4_HEIGHT_INNER);

      block.renderAnswerHeight = block.answerHeight + block.marginBottom  - top;
      page.blocks.push(block);
      pages.push(page);

      for (let i = 0; i < blankPageNum; i++) {
        page = newLayoutPage(0);
        pages.push(page);
      }
      lastOffsetY = top % A4_HEIGHT_INNER;
      page = newLayoutPage(lastOffsetY);

    }
  };
  traversal(raw, visit);

  if (page.blocks.length > 0 || page.top > 0) {
    pages.push(page);
  }

  return pages;
}

export { LayoutBlock, LayoutPage, LayoutPages, layout }